import { isNumber, isString } from "lodash";
import { format } from "mathjs";

import { ColumnType, FormulaResultType, ILookupTypeOptions, NumberFormat, ITypeOptions } from "../types/table-type";
import {
  FilterType,
  FilterCondition,
  OperatorType,
  AttachmentFileTypeOptions,
  DateComparisonOptions,
} from "../types/form-type";

export const HIDE_FILTER_INPUT_BY_CONDITION: { [key in FilterCondition]?: boolean } = {
  [FilterCondition.IS_EMPTY]: true,
  [FilterCondition.IS_NOT_EMPTY]: true,
};

export const DATE_COMPARISON_OPTIONS = [
  { value: DateComparisonOptions.TODAY, name: "controls.filters.today" },
  { value: DateComparisonOptions.TOMORROW, name: "controls.filters.tomorrow" },
  { value: DateComparisonOptions.YESTERDAY, name: "controls.filters.yesterday" },
  { value: DateComparisonOptions.ONE_WEEK_AGO, name: "controls.filters.oneWeekAgo" },
  { value: DateComparisonOptions.ONE_WEEK_FROM_NOW, name: "controls.filters.oneWeekFromNow" },
  { value: DateComparisonOptions.ONE_MONTH_AGO, name: "controls.filters.oneMonthAgo" },
  {
    value: DateComparisonOptions.ONE_MONTH_FROM_NOW,
    name: "controls.filters.oneMonthFromNow",
  },
  { value: DateComparisonOptions.NUM_DAYS_AGO, name: "controls.filters.numOfDaysAgo" },
  {
    value: DateComparisonOptions.NUM_DAYS_FROM_NOW,
    name: "controls.filters.numOfDaysFromNow",
  },
  { value: DateComparisonOptions.EXACT_DATE, name: "controls.filters.exactDate" },
];

export const SHOW_DATE_COMPARISON_INPUT: { [key in FilterCondition]?: boolean } = {
  [FilterCondition.EQUALS]: true,
  [FilterCondition.NOT_EQUAL]: true,
  [FilterCondition.LESS_THAN]: true,
  [FilterCondition.GREATER_THAN]: true,
  [FilterCondition.LESS_THAN_OR_EQUAL]: true,
  [FilterCondition.GREATER_THAN_OR_EQUAL]: true,
};

export const SHOW_DATE_DATE_INPUT: { [key in DateComparisonOptions]?: boolean } = {
  [DateComparisonOptions.EXACT_DATE]: true,
};
export const SHOW_DATE_NUMBER_INPUT: { [key in DateComparisonOptions]?: boolean } = {
  [DateComparisonOptions.NUM_DAYS_AGO]: true,
  [DateComparisonOptions.NUM_DAYS_FROM_NOW]: true,
};

export const FILE_TYPE_FILTERS = [
  { name: "Text", value: AttachmentFileTypeOptions.TEXT },
  { name: "Image", value: AttachmentFileTypeOptions.IMAGE },
  { name: "PDF", value: AttachmentFileTypeOptions.PDF },
  { name: "Excel", value: AttachmentFileTypeOptions.EXCEL },
];

const TEXT_FILTERS = [
  { value: FilterCondition.EQUALS, name: "controls.filter.equals" },
  { value: FilterCondition.NOT_EQUAL, name: "controls.filter.notEqual" },
  { value: FilterCondition.STARTS_WITH, name: "controls.filter.startsWith" },
  { value: FilterCondition.ENDS_WITH, name: "controls.filter.endsWith" },
  { value: FilterCondition.CONTAINS, name: "controls.filter.contains" },
  { value: FilterCondition.NOT_CONTAINS, name: "controls.filter.notContains" },
  { value: FilterCondition.IS_EMPTY, name: "controls.filter.isEmpty" },
  { value: FilterCondition.IS_NOT_EMPTY, name: "controls.filter.isNotEmpty" },
];

const EMAIL_FILTERS = [
  { value: FilterCondition.EQUALS, name: "controls.filter.equals" },
  { value: FilterCondition.NOT_EQUAL, name: "controls.filter.notEqual" },
  { value: FilterCondition.STARTS_WITH, name: "controls.filter.startsWith" },
  { value: FilterCondition.ENDS_WITH, name: "controls.filter.endsWith" },
  { value: FilterCondition.CONTAINS, name: "controls.filter.contains" },
  { value: FilterCondition.NOT_CONTAINS, name: "controls.filter.notContains" },
  { value: FilterCondition.IS_EMPTY, name: "controls.filter.isEmpty" },
  { value: FilterCondition.IS_NOT_EMPTY, name: "controls.filter.isNotEmpty" },
];

const NUMBER_FILTERS = [
  { value: FilterCondition.EQUALS, name: "controls.filter.=" },
  { value: FilterCondition.NOT_EQUAL, name: "controls.filter.≠" },
  { value: FilterCondition.LESS_THAN, name: "controls.filter.<" },
  { value: FilterCondition.LESS_THAN_OR_EQUAL, name: "controls.filter.≤" },
  { value: FilterCondition.GREATER_THAN, name: "controls.filter.>" },
  { value: FilterCondition.GREATER_THAN_OR_EQUAL, name: "controls.filter.≥" },
  { value: FilterCondition.IS_EMPTY, name: "controls.filter.isEmpty" },
  { value: FilterCondition.IS_NOT_EMPTY, name: "controls.filter.isNotEmpty" },
];

const DATE_FILTERS = [
  { value: FilterCondition.EQUALS, name: "controls.filters.is" },
  { value: FilterCondition.NOT_EQUAL, name: "controls.filters.isNot" },
  { value: FilterCondition.LESS_THAN, name: "controls.filters.isBefore" },
  { value: FilterCondition.GREATER_THAN, name: "controls.filters.isAfter" },
  { value: FilterCondition.LESS_THAN_OR_EQUAL, name: "controls.filters.isOnorBefore" },
  { value: FilterCondition.GREATER_THAN_OR_EQUAL, name: "controls.filters.isOnorAfter" },
  { value: FilterCondition.IS_EMPTY, name: "controls.filter.isEmpty" },
  { value: FilterCondition.IS_NOT_EMPTY, name: "controls.filter.isNotEmpty" },
];

const STATUS_FILTERS = [
  { value: FilterCondition.EQUALS, name: "controls.filters.is" },
  { value: FilterCondition.NOT_EQUAL, name: "controls.filters.isNot" },
  { value: FilterCondition.EQUALS_ANY_OF, name: "controls.filters.isAnyOf" },
  { value: FilterCondition.EQUALS_NONE_OF, name: "controls.filters.isNoneOf" },
];

const SELECT_FILTERS = [
  { value: FilterCondition.EQUALS, name: "controls.filters.is" },
  { value: FilterCondition.NOT_EQUAL, name: "controls.filters.isNot" },
  { value: FilterCondition.EQUALS_ANY_OF, name: "controls.filters.isAnyOf" },
  { value: FilterCondition.EQUALS_NONE_OF, name: "controls.filters.isNoneOf" },
  { value: FilterCondition.IS_EMPTY, name: "controls.filter.isEmpty" },
  { value: FilterCondition.IS_NOT_EMPTY, name: "controls.filter.isNotEmpty" },
];

const MULTI_SELECT_FILTERS = [
  { value: FilterCondition.HAS_ANY_OF, name: "controls.filters.hasAnyOf" },
  { value: FilterCondition.HAS_ALL_OF, name: "controls.filters.hasAllOf" },
  { value: FilterCondition.HAS_NONE_OF, name: "controls.filters.hasNoneOf" },
  { value: FilterCondition.EQUALS, name: "controls.filters.isExactly" },
  { value: FilterCondition.IS_EMPTY, name: "controls.filter.isEmpty" },
  { value: FilterCondition.IS_NOT_EMPTY, name: "controls.filter.isNotEmpty" },
];

const ATTACHMENT_FILTERS = [
  { value: FilterCondition.FILE_NAME, name: "controls.filter.fileName" },
  { value: FilterCondition.FILE_TYPE, name: "controls.filter.fileType" },
  { value: FilterCondition.IS_EMPTY, name: "controls.filter.isEmpty" },
  { value: FilterCondition.IS_NOT_EMPTY, name: "controls.filter.isNotEmpty" },
];

const LOOKUP_FILTERS = [
  { value: FilterCondition.EQUALS, name: "controls.filter.equals" },
  { value: FilterCondition.NOT_EQUAL, name: "controls.filter.notEqual" },
  { value: FilterCondition.CONTAINS, name: "controls.filter.contains" },
  { value: FilterCondition.NOT_CONTAINS, name: "controls.filter.notContains" },
  { value: FilterCondition.IS_EMPTY, name: "controls.filter.isEmpty" },
  { value: FilterCondition.IS_NOT_EMPTY, name: "controls.filter.isNotEmpty" },
];

const CHECKBOX_FILTERS = [{ value: FilterCondition.EQUALS, name: "controls.filters.is" }];

export const OPERATOR_OPTIONS = [
  { value: OperatorType.AND, name: "controls.filter.and" },
  { value: OperatorType.OR, name: "controls.filter.or" },
];

export const CONDITION_OPTIONS: {
  [key in FilterType]: Array<{ value: FilterCondition; name: string }>;
} = {
  [FilterType.TEXT]: TEXT_FILTERS,
  [FilterType.PHONE]: TEXT_FILTERS,
  [FilterType.EMAIL]: EMAIL_FILTERS,
  [FilterType.NUMBER]: NUMBER_FILTERS,
  [FilterType.DATE]: DATE_FILTERS,
  [FilterType.STATUS]: STATUS_FILTERS,
  [FilterType.SELECT]: SELECT_FILTERS,
  [FilterType.MULTI_SELECT]: MULTI_SELECT_FILTERS,
  [FilterType.ATTACHMENT]: ATTACHMENT_FILTERS,
  [FilterType.LOOKUP]: LOOKUP_FILTERS,
  [FilterType.CHECKBOX]: CHECKBOX_FILTERS,
  [FilterType.RECORD_REFERENCE]: TEXT_FILTERS,
  [FilterType.COLLABORATOR]: MULTI_SELECT_FILTERS,
  [FilterType.CREATED_BY]: SELECT_FILTERS,
};

/**
 * 以下类型的 filterCondition 对应的 filterString 是 长度为1的数组
 */
export const FILTERSTRING_IS_ARRAY_ONE_BY_FILTERCONDITION: Partial<Record<FilterCondition, boolean>> = {
  [FilterCondition.EQUALS]: true,
  [FilterCondition.NOT_EQUAL]: true,
};

export const convertLookupTypeOptions = (typeOptions: ILookupTypeOptions): ITypeOptions => {
  const lookupTypeOptions = typeOptions.lookupTypeOptions;
  return lookupTypeOptions
    ? ({ ...lookupTypeOptions, type: typeOptions.lookupColumnType } as ITypeOptions)
    : ({ type: typeOptions.lookupColumnType } as ITypeOptions);
};

export const getTypeOptions = (typeOptions: ITypeOptions) =>
  typeOptions.type === ColumnType.LOOKUP
    ? convertLookupTypeOptions(typeOptions as ITypeOptions<ColumnType.LOOKUP>)
    : typeOptions;

export const getFinalColumnType = (typeOptions: ITypeOptions, ignoreLookup?: boolean): ColumnType => {
  if (typeOptions.type === ColumnType.FORMULA || typeOptions.type === ColumnType.ROLLUP) {
    const { resultType, numberFormat } = typeOptions as ITypeOptions<ColumnType.FORMULA>;
    switch (resultType) {
      case FormulaResultType.TEXT:
        return ColumnType.TEXT;
      case FormulaResultType.NUMBER:
        switch (numberFormat) {
          case NumberFormat.CURRENCY:
            return ColumnType.CURRENCY;
          case NumberFormat.DECIMAL:
            return ColumnType.NUMBER;
          default:
            return ColumnType.NUMBER;
        }
      case FormulaResultType.DATE:
        return ColumnType.DATETIME;
      default:
        return ColumnType.TEXT;
    }
  }
  if (!ignoreLookup && typeOptions.type === ColumnType.LOOKUP) {
    const { lookupColumnType } = typeOptions as ITypeOptions<ColumnType.LOOKUP>;
    if (lookupColumnType === ColumnType.FORMULA || lookupColumnType === ColumnType.ROLLUP) {
      const lookupTypeOptions = getTypeOptions(typeOptions) as ITypeOptions;
      return getFinalColumnType(lookupTypeOptions);
    }
    return lookupColumnType;
  }
  return typeOptions.type;
};

export const getFormattedAutoNumber = (value: string) => {
  const parseValue = parseInt(value);

  if (isNaN(parseValue)) return null;

  if (!isFinite(parseValue)) return null;

  return parseValue as number;
};

export const getFormattedCurrency = (value: number, typeOptions: ITypeOptions<ColumnType.CURRENCY>) => {
  if (!isNumber(value)) return null;

  // if value is Infinity, -Infinity, or NaN, we should not format it
  if (!isFinite(value)) return `${value}`;

  const precision = typeOptions ? typeOptions.precision : 0;
  const currencyFormat = typeOptions.currencyFormat;
  const isNegative = value < 0;
  const currencyFormatted = Number(Math.abs(value).toFixed(precision)).toLocaleString("en", {
    maximumFractionDigits: precision,
    minimumFractionDigits: precision,
  });

  return `${isNegative ? "-" : ""}${currencyFormat}${currencyFormatted}`;
};

export const isInvalidTypedChar = (key: string | null, extraChar: string = "") =>
  !!key && !isNumberAllowKeyPress[key] && `${extraChar}1234567890.-,。`.indexOf(key) < 0;

export const isInvalidTypedCharForInput = (
  event: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>,
  extraChar: string = ""
) => {
  const { keyCode, key, ctrlKey, metaKey } = event;

  const functionalKeyCode = !!keyCode && (metaKey || ctrlKey);

  return isInvalidTypedChar(key, extraChar) && !functionalKeyCode;
};
export const isNumberAllowKeyPress: { [key: string]: any } = {
  Backspace: 1,
  Delete: 1,
  ArrowUp: 1,
  ArrowLeft: 1,
  ArrowDown: 1,
  ArrowRight: 1,
  Tab: 1,
};

export const parseStringToFloat = (targetValue: string) => {
  if (!isString(targetValue)) return null;

  const parsed = parseFloat(targetValue.replace(/[^0-9.-]/g, ""));
  if (isNaN(parsed)) return null;

  return parsed;
};

export const getFormattedNumber = (value: number, typeOptions: ITypeOptions<ColumnType.NUMBER>) => {
  const precision = typeOptions ? typeOptions.precision : 0;
  const { numberFormat } = typeOptions;

  if (isNaN(value)) return "";

  switch (numberFormat) {
    case NumberFormat.DECIMAL:
      return isNumber(value) ? value.toFixed(precision) : "";
    case NumberFormat.PERCENTAGE:
      return getFormattedPercentage(value, typeOptions);
    case NumberFormat.DECIMAL_WITH_COMMAS:
      return getFormattedNumberWithCommas(value, typeOptions);
    default:
      return isNumber(value) ? value.toFixed(precision) : "";
  }
};

export const getFormattedPercentage = (value: number, typeOptions: ITypeOptions<ColumnType.NUMBER>) => {
  const precision = typeOptions ? typeOptions.precision : 0;
  if (typeof value !== "number" || isNaN(value)) return "";
  return `${(value * 100).toFixed(precision)}%`;
};

export const getFormattedNumberWithCommas = (value: number, typeOptions: ITypeOptions<ColumnType.NUMBER>) => {
  const { precision } = typeOptions;
  return typeof value === "number"
    ? value.toLocaleString("en", {
        maximumFractionDigits: precision,
        minimumFractionDigits: precision,
      })
    : "";
};

export const parseStringToFloatForNumber = (targetValue: string, typeOptions: ITypeOptions<ColumnType.NUMBER>) => {
  if (!isString(targetValue)) return null;
  const { numberFormat } = typeOptions;

  const parsed = targetValue.replace(/[^0-9.-]/g, "");
  let parsedFloat: number;

  switch (numberFormat) {
    case NumberFormat.DECIMAL:
      parsedFloat = parseFloat(parsed);
      break;
    case NumberFormat.PERCENTAGE:
      parsedFloat = parseFloat(format(parseFloat(parsed) / 100, { precision: 14 }));
      break;
    case NumberFormat.DECIMAL_WITH_COMMAS:
      parsedFloat = parseFloat(parsed);
      break;
    default:
      parsedFloat = parseFloat(parsed);
      break;
  }

  if (isNaN(parsedFloat)) return null;

  return parsedFloat;
};

export const isInvalidTypedCharForNumberInput = (
  event: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>,
  typeOptions: ITypeOptions<ColumnType.NUMBER>,
  suppressSymbol?: boolean
) => {
  const { keyCode, key, ctrlKey, metaKey } = event;

  const functionalKeyCode = !!keyCode && (metaKey || ctrlKey);

  return isInvalidTypedCharForNumber(key, typeOptions, suppressSymbol) && !functionalKeyCode;
};

export const isInvalidTypedCharForNumber = (
  key: string | null,
  typeOptions: ITypeOptions<ColumnType.NUMBER>,
  suppressSymbol?: boolean
) => {
  const { numberFormat } = typeOptions;

  let extraChar;
  if (suppressSymbol) {
    extraChar = "";
  } else {
    switch (numberFormat) {
      case NumberFormat.PERCENTAGE:
        extraChar = "%";
        break;
      case NumberFormat.DECIMAL_WITH_COMMAS:
        extraChar = ",";
        break;
      default:
        extraChar = "";
        break;
    }
  }

  return !!key && !isNumberAllowKeyPress[key] && `${extraChar}1234567890.-。`.indexOf(key) < 0;
};

export const isInvalidTypedCharForRating = (event: KeyboardEvent | React.KeyboardEvent<HTMLInputElement>) => {
  const { key } = event;
  return !!key && !"1234567890".includes(key);
};

type NotNull<T> = T extends null ? never : T;
export const isNotNull = <T>(arg: T): arg is NotNull<T> => arg !== null;
